import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Brain, X } from 'lucide-react';
import { useComprehensionAssessmentContext } from '@/components/assessment/comprehension-assessment-provider';
import { ComprehensionAssessmentModal } from '@/components/assessment/comprehension-assessment-modal';
import { useAuth } from '@/components/auth/AuthProvider';
import { comprehensionService } from '@/services/comprehension-service';

export function AssessmentNotificationSystem() {
  const { user } = useAuth();
  const {
    shouldShowAssessment,
    profile,
    getDaysUntilNextAssessment,
    isEligibleForReassessment
  } = useComprehensionAssessmentContext();

  const [showModal, setShowModal] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [isNotificationDismissed, setIsNotificationDismissed] = useState(false);
  const [hasActiveSession, setHasActiveSession] = useState(false);

  const daysUntilNext = getDaysUntilNextAssessment();
  const isOverdue = daysUntilNext !== null && daysUntilNext < 0;
  const isDueSoon = daysUntilNext !== null && daysUntilNext <= 7 && daysUntilNext > 0;

  // Check for active session
  useEffect(() => {
    if (user?.id) {
      const activeSession = comprehensionService.hasActiveSession(user.id);
      setHasActiveSession(activeSession);
    }
  }, [user?.id]);

  // Show notification for due assessments
  useEffect(() => {
    if ((isOverdue || isDueSoon || isEligibleForReassessment) && !isNotificationDismissed) {
      const timer = setTimeout(() => {
        setShowNotification(true);
      }, 2000); // Show after 2 seconds

      return () => clearTimeout(timer);
    }
  }, [isOverdue, isDueSoon, isEligibleForReassessment, isNotificationDismissed]);

  const handleTakeAssessment = () => {
    setShowNotification(false);
    setShowModal(true);
  };

  const handleDismissNotification = () => {
    setShowNotification(false);
    setIsNotificationDismissed(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setIsNotificationDismissed(true);
    // Recheck session state after modal closes
    if (user?.id) {
      const activeSession = comprehensionService.hasActiveSession(user.id);
      setHasActiveSession(activeSession);
    }
  };

  if (!showNotification && !shouldShowAssessment) {
    return (
      <>
        <ComprehensionAssessmentModal 
          open={showModal} 
          onOpenChange={handleModalClose} 
        />
      </>
    );
  }

  return (
    <>
      {showNotification && (
        <div className="fixed top-4 right-4 z-50 w-96 animate-in slide-in-from-right">
          <Alert className={`border-l-4 shadow-lg ${
            isOverdue 
              ? 'border-l-red-500 bg-red-50 border-red-200' 
              : isDueSoon 
                ? 'border-l-orange-500 bg-orange-50 border-orange-200' 
                : 'border-l-blue-500 bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-start justify-between w-full">
              <div className="flex items-start space-x-3 flex-1">
                <div className={`p-2 rounded-lg ${
                  isOverdue 
                    ? 'bg-red-100' 
                    : isDueSoon 
                      ? 'bg-orange-100' 
                      : 'bg-blue-100'
                }`}>
                  <Brain className={`h-5 w-5 ${
                    isOverdue 
                      ? 'text-red-600' 
                      : isDueSoon 
                        ? 'text-orange-600' 
                        : 'text-blue-600'
                  }`} />
                </div>
                
                <div className="flex-1 space-y-1">
                  <div className="flex items-center space-x-2">
                    <h4 className={`font-semibold text-sm ${
                      isOverdue 
                        ? 'text-red-800' 
                        : isDueSoon 
                          ? 'text-orange-800' 
                          : 'text-blue-800'
                    }`}>
                      {isOverdue 
                        ? 'Assessment Overdue' 
                        : isDueSoon 
                          ? 'Assessment Due Soon' 
                          : 'Assessment Available'
                      }
                    </h4>
                    {isOverdue && (
                      <Badge variant="destructive" className="text-xs">
                        {Math.abs(daysUntilNext || 0)} days overdue
                      </Badge>
                    )}
                    {isDueSoon && (
                      <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-800">
                        Due in {daysUntilNext} days
                      </Badge>
                    )}
                  </div>
                  
                  <AlertDescription className={`text-xs ${
                    isOverdue 
                      ? 'text-red-700' 
                      : isDueSoon 
                        ? 'text-orange-700' 
                        : 'text-blue-700'
                  }`}>
                    {isEligibleForReassessment 
                      ? "Time to update your comprehension profile for better personalized learning."
                      : "Take a quick assessment to personalize your learning experience."
                    }
                  </AlertDescription>
                  
                  <div className="flex space-x-2 pt-2">
                    <Button 
                      size="sm" 
                      onClick={handleTakeAssessment}
                      className={`h-7 text-xs ${
                        isOverdue 
                          ? 'bg-red-600 hover:bg-red-700' 
                          : isDueSoon 
                            ? 'bg-orange-600 hover:bg-orange-700' 
                            : ''
                      }`}
                    >
                      {hasActiveSession ? 'Resume Assessment' : 'Take Assessment'}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={handleDismissNotification}
                      className="h-7 text-xs"
                    >
                      Later
                    </Button>
                  </div>
                </div>
              </div>
              
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismissNotification}
                className="h-6 w-6 p-0 ml-2"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </Alert>
        </div>
      )}

      <ComprehensionAssessmentModal 
        open={showModal} 
        onOpenChange={handleModalClose} 
      />
    </>
  );
}

// Hook for getting assessment notification status (for UI indicators)
export function useAssessmentNotifications() {
  const {
    shouldShowAssessment,
    getDaysUntilNextAssessment,
    isEligibleForReassessment
  } = useComprehensionAssessmentContext();

  const daysUntilNext = getDaysUntilNextAssessment();
  const isOverdue = daysUntilNext !== null && daysUntilNext < 0;
  const isDueSoon = daysUntilNext !== null && daysUntilNext <= 7 && daysUntilNext > 0;

  return {
    hasNotification: shouldShowAssessment || isOverdue || isDueSoon,
    isOverdue,
    isDueSoon,
    isEligibleForReassessment,
    daysUntilNext,
    notificationCount: (shouldShowAssessment || isOverdue || isDueSoon) ? 1 : 0,
    urgencyLevel: isOverdue ? 'high' : isDueSoon ? 'medium' : 'low'
  };
}
